import React from "react";
import moment from "moment";
import styles from "./FHIRPatient.module.scss";
import RecordOverrideOptions from "../../routes/NewSearchRecords/SearchRecordTable/RecordOverrideOptions";
import { overrideSummaryForBirthDate } from "../../routes/NewSearchRecords/SearchRecordTable/RecordOverrideUtil";
import { handleMultipleAttributeOverrideSingleValued } from "../../routes/NewSearchRecords/SearchRecordTable/MultipleAttributeOverride/Utils";
import Modal from "../../components/ModalPopup";
import ButtonList from "../../components/ButtonList";
import Button from "../../components/Button";
import OverrideAuditInputModal from "../../routes/NewSearchRecords/SearchRecordTable/OverrideAuditInputModal";
import {
  dobOverriddenByString,
  dobOverrideReasonString,
  dobOverrideTimestampString
} from "../../routes/NewSearchRecords/SearchRecordTable/GoldenRecordExclusion/Utils";
import { renderFieldWithDiff } from "./Utils";

const BirthDate = ({
  data,
  summary: isSummary,
  extensions: extensions,
  oncRecordOverrideIconClick,
  overrideExtension,
  isInvisible = false,
  isNewQuarantine = false,
  isMultipleAttributeOverride = false,
  setAttributeOverrides,
  attributeOverrides,
  hasDiff = false,
  diffInfo = null,
  isPreviewMode = false
}) => {
  const [
    replaceRecordOverrideDialog,
    setReplaceRecordOverrideDialog
  ] = React.useState(false);
  const [
    recordOverrideDialogContent,
    setRecordOverrideDialogContent
  ] = React.useState({});
  const [value, setValue] = React.useState("");
  const [valueChanged, setValueChanged] = React.useState(false);
  const [showAuditModal, setShowAuditModal] = React.useState(false);
  const [pendingConfirmDetails, setPendingConfirmDetails] = React.useState(
    null
  );

  if (isSummary && !data) {
    return <div>Date Of Birth: [Empty]</div>;
  }

  if (isSummary) {   
    if (isPreviewMode && hasDiff && diffInfo) {
      const priorDOB = diffInfo.prior ? diffInfo.prior : null;
      const changeType = diffInfo.changeType || null;
      const currentDOB = data ? moment(data).format("L") : "-";
      const priorDOBDisplay = priorDOB ? moment(priorDOB).format("L") : "-";
      return (
        <div>
          Date Of Birth: {renderFieldWithDiff(currentDOB, priorDOBDisplay, changeType, isPreviewMode, hasDiff)}
        </div>
      );
    }
    return <div>Date Of Birth: {data && moment(data).format("L")}</div>;
  }
  const dobMap = {
    Field: "Date of Birth",
    "Date Of Birth": data ? moment(data).format("L") : "-",
    "Mark as": "",
    originalFieldName: "birthDate",
    originalFieldData: data,
    originalFieldDataCopy: data
  };

  const prepareOverrideOriginalValue = type => {
    const extensionMap = overrideSummaryForBirthDate(
      data,
      combinedOverrideExtension,
      type
    );
    oncRecordOverrideIconClick(extensionMap);
  };

  const actionHandler = detailsMap => {
    if (isMultipleAttributeOverride) {
      handleMultipleAttributeOverrideSingleValued(
        detailsMap,
        "birthDate",
        attributeOverrides,
        setAttributeOverrides
      );
      setReplaceRecordOverrideDialog(false);
    } else {
      oncRecordOverrideIconClick(detailsMap);
    }
  };

  const handleReplaceOverride = detailsMap => {
    if (detailsMap["Mark as"] && detailsMap["Mark as"] === "Replace") {
      if (detailsMap.originalFieldData) {
        setValue(detailsMap.originalFieldData);
      }
      setRecordOverrideDialogContent(detailsMap);
      setReplaceRecordOverrideDialog(true);
    } else {
      actionHandler(detailsMap);
    }
  };

  const handleConfirm = () => {
    const detailsMap = { ...recordOverrideDialogContent };
    detailsMap.originalFieldData = value;
    if (isMultipleAttributeOverride) {
      // Directly proceed to next step without audit modal
      actionHandler(detailsMap);
    } else {
      setPendingConfirmDetails(detailsMap);
      setShowAuditModal(true);
      // Do NOT call actionHandler(detailsMap) directly here
    }
  };

  const handleAuditSubmit = auditInfo => {
    if (pendingConfirmDetails) {
      const updatedDetails = {
        ...pendingConfirmDetails,
        auditInfoList: auditInfo
      };
      actionHandler(updatedDetails);
      setPendingConfirmDetails(null);
    }
    setShowAuditModal(false);
  };

  const dobAuditUrls = [
    dobOverriddenByString,
    dobOverrideReasonString,
    dobOverrideTimestampString
  ];

  const dobAuditExtensions = (extensions || []).filter(e =>
    dobAuditUrls.includes(e.url)
  );

  // Combine main overrideExtension with the audit extensions, avoiding duplicates
  const combinedOverrideExtension = [
    ...(overrideExtension || []),
    ...dobAuditExtensions.filter(
      auditExt =>
        !(overrideExtension || []).some(mainExt => mainExt.url === auditExt.url)
    )
  ];

  const replaceOverrideModal = () => {
    return (
      <Modal
        open={replaceRecordOverrideDialog}
        onClose={() => setReplaceRecordOverrideDialog(false)}
      >
        <h3 style={{ textAlign: "center" }}>Replace override</h3>
        <br />
        <br />
        <div>
          {recordOverrideDialogContent && (
            <table className={"recOvrTable"}>
              <tr>
                <td>Date Of Birth</td>
              </tr>
              <tr>
                <td>
                  <input
                    type="date"
                    value={value}
                    onChange={e => {
                      setValue(e.target.value);
                      if (!valueChanged) {
                        setValueChanged(true);
                      }
                    }}
                  />
                </td>
              </tr>
            </table>
          )}
        </div>
        <br />
        <br />
        <div style={{ marginTop: "1rem" }}>
          <ButtonList
            style={{
              display: "flex",
              justifyContent: "center"
            }}
          >
            <Button
              primary
              disabled={!valueChanged}
              onClick={() => handleConfirm()}
            >
              Confirm Changes
            </Button>
            <Button
              onClick={() => {
                setValueChanged(false);
                setReplaceRecordOverrideDialog(false);
              }}
            >
              Cancel
            </Button>
          </ButtonList>
        </div>
      </Modal>
    );
  };

  const dobTimeStamp = extensions
    ? extensions.filter(e => e.url.includes("birthTimeStamp"))
    : "";

  const priorDOB = (isPreviewMode && hasDiff && diffInfo && diffInfo.prior) ? diffInfo.prior : null;
  const changeType = (diffInfo && isPreviewMode && hasDiff) ? diffInfo.changeType : null;
  const currentDOB = data ? moment(data).format("L") : "-";
  const priorDOBDisplay = priorDOB ? moment(priorDOB).format("L") : "-";

  return (
    <>
      {/* AUDIT MODAL */}
      {showAuditModal && !isMultipleAttributeOverride && (
        <OverrideAuditInputModal
          isOpen={showAuditModal}
          onCancel={() => setShowAuditModal(false)}
          onSubmit={handleAuditSubmit}
        />
      )}
      <div>
        <h4>Date Of Birth</h4>
        {isPreviewMode && hasDiff && diffInfo ? 
          renderFieldWithDiff(currentDOB, priorDOBDisplay, changeType, isPreviewMode, hasDiff)
        : (
          data ? (
            data.split(",").map((d, idx) => <div key={idx}>{moment(d).format("L")}</div>)
          ) : (
            <div>-</div>
          )
        )}
        <div>
          <h6>
            {dobTimeStamp[0]
              ? "Source Timestamp: " +
                moment(dobTimeStamp[0].valueDateTime)
                  .tz("America/Chicago")
                  .format("l, LTS zz")
              : ""}
          </h6>
        </div>
      </div>
      {oncRecordOverrideIconClick && !isInvisible && !isNewQuarantine && (
        <div className={styles.actionColumn}>
          <h4>Action</h4>
          <RecordOverrideOptions
            detailsMap={dobMap}
            onClickRecordOverrideAction={detailsMap =>
              handleReplaceOverride(detailsMap)
            }
            disableInactiiveAndInvalid={true}
            onClickRecordOverrideButton={type =>
              prepareOverrideOriginalValue(type)
            }
            extension={combinedOverrideExtension}
            isMultipleAttributeOverride={isMultipleAttributeOverride}
            selectedOverrides={attributeOverrides.birthDate}
          />
        </div>
      )}
      {replaceOverrideModal()}
    </>
  );
};

export default BirthDate;
